/**
 * CollapsibleNavLayout — responsive shell for the learner nav sidebar + main content.
 *
 * Above `collapseBreakpoint` the lesson nav renders inline as a fixed-width
 * sidebar. Below it, the sidebar collapses behind a hamburger in a top bar and
 * slides in from the left as a drawer over a scrim (scrim-tap / Esc / selecting
 * a lesson closes it). The top bar always sits at the top of this component, so
 * when a consumer renders a Save & Exit bar above it the bar stays on top.
 *
 * Shared so the course preview, lesson preview, and export player stay in sync.
 */

import React, { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useTheme } from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';

import { useIsMobileViewport } from './hooks/useIsMobileViewport';

// Below this viewport width the lesson panel collapses behind a hamburger.
export const DEFAULT_NAV_COLLAPSE_BREAKPOINT = 1100;

export interface CollapsibleNavLayoutProps {
  // Render the nav content. Receives `closeDrawer` so lesson selection can
  // dismiss the drawer in the collapsed layout.
  renderNav: (closeDrawer: () => void) => ReactNode;
  // Main content (canvas) plus any overlays (chat widget) that should live
  // inside the positioned stage.
  children: ReactNode;
  // Optional user toolbar. Rendered floating (top-right) when expanded and
  // inline inside the top bar (right of the hamburger) when collapsed.
  renderToolbar?: (variant: 'floating' | 'inline') => ReactNode;
  collapseBreakpoint?: number;
}

export function CollapsibleNavLayout({
  renderNav,
  children,
  renderToolbar,
  collapseBreakpoint = DEFAULT_NAV_COLLAPSE_BREAKPOINT,
}: CollapsibleNavLayoutProps): React.ReactElement {
  const { t } = useTranslation();
  const { tokens } = useTheme();
  const isCollapsed = useIsMobileViewport(collapseBreakpoint);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Collapse back to inline → make sure the drawer state is reset.
  useEffect(() => {
    if (!isCollapsed) setIsDrawerOpen(false);
  }, [isCollapsed]);

  // Esc closes the open drawer.
  useEffect(() => {
    if (!isDrawerOpen) return undefined;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsDrawerOpen(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isDrawerOpen]);

  const closeDrawer = () => setIsDrawerOpen(false);
  const nav = renderNav(closeDrawer);

  const stage: CSSProperties = {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    position: 'relative',
    overflow: 'hidden',
  };

  const sidebar: CSSProperties = {
    width: tokens.layout.sidebar.width,
    minWidth: tokens.layout.sidebar.minWidth,
    maxWidth: tokens.layout.sidebar.maxWidth,
    height: '100%',
    overflowY: 'auto',
    backgroundColor: tokens.backgrounds.sidebar,
  };

  const column: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    // Anchor the full-height drawer + scrim, which overlay the top bar too.
    position: 'relative',
  };
  const topBar: CSSProperties = {
    boxSizing: 'border-box',
    flexShrink: 0,
    width: '100%',
    height: '56px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    // Right padding wider so the toolbar set sits ~24px off the edge.
    padding: `0 ${tokens.spacing.xl} 0 ${tokens.spacing.md}`,
    backgroundColor: tokens.backgrounds.sidebar,
    // Above the stage so the toolbar's popovers render over the content;
    // below the scrim/drawer (100/101) so they cover it when the drawer opens.
    position: 'relative',
    zIndex: 90,
  };
  const iconButton: CSSProperties = {
    boxSizing: 'border-box',
    width: '44px',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    border: 'none',
    borderRadius: tokens.radius.sm,
    background: 'transparent',
    color: tokens.navigation.titleColor,
    cursor: 'pointer',
  };
  const scrim: CSSProperties = {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 100,
  };
  const drawer: CSSProperties = {
    boxSizing: 'border-box',
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    // Fixed at the panel's min-width in collapsed mode — always 340px. The
    // 100vw cap is only a safety net for screens narrower than 340px (no real
    // device); it never shrinks the drawer at realistic widths.
    width: tokens.layout.sidebar.minWidth,
    maxWidth: '100vw',
    backgroundColor: tokens.backgrounds.sidebar,
    // Only while open — when closed the drawer is off-screen left and the
    // shadow would otherwise bleed onto the left edge of the viewport.
    boxShadow: isDrawerOpen ? '2px 0 12px rgba(0, 0, 0, 0.3)' : 'none',
    transition: 'transform 250ms ease',
    overflow: 'hidden',
    zIndex: 101,
    transform: isDrawerOpen ? 'translateX(0)' : 'translateX(-100%)',
    display: 'flex',
    flexDirection: 'column',
  };
  // Sits where the top bar would be; holds the close (X) control on the right.
  // Layered above the body so the X stays visible/clickable over the pulled-up
  // nav content.
  const drawerHeader: CSSProperties = {
    flexShrink: 0,
    height: '56px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '0 8px',
    position: 'relative',
    zIndex: 1,
  };
  const drawerBody: CSSProperties = {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    // The nav card carries its own top padding (statusCard outerPadding); the
    // X-row already provides the top gap, so pull the body up to absorb it.
    marginTop: `-${tokens.spacing.xl}`,
  };

  // NA-259: both layouts share one skeleton so `children` (the lesson canvas
  // and its scroll container) keeps a stable tree position when the collapse
  // breakpoint flips — a structural swap here remounts the canvas and resets
  // the learner's scroll position to the top.
  return (
    <div style={column}>
      {isCollapsed && (
        <div style={topBar}>
          {!isDrawerOpen && (
            <>
              <button
                type="button"
                style={iconButton}
                aria-label={t('lesson_navigation.open')}
                aria-expanded={false}
                onClick={() => setIsDrawerOpen(true)}
              >
                <HamburgerIcon />
              </button>
              {renderToolbar?.('inline')}
            </>
          )}
        </div>
      )}
      <div style={stage}>
        {!isCollapsed && <aside style={sidebar}>{nav}</aside>}
        {children}
        {!isCollapsed && renderToolbar?.('floating')}
      </div>
      {isCollapsed && isDrawerOpen && (
        <div style={scrim} onClick={closeDrawer} aria-hidden="true" />
      )}
      {isCollapsed && (
        <aside style={drawer}>
          <div style={drawerHeader}>
            <button
              type="button"
              style={iconButton}
              aria-label={t('lesson_navigation.close')}
              onClick={closeDrawer}
            >
              <CloseIcon />
            </button>
          </div>
          <div style={drawerBody}>{nav}</div>
        </aside>
      )}
    </div>
  );
}

CollapsibleNavLayout.displayName = 'CollapsibleNavLayout';

function HamburgerIcon(): React.ReactElement {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M3 6h18M3 12h18M3 18h18" />
    </svg>
  );
}

function CloseIcon(): React.ReactElement {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}
